import SendIcon from '@mui/icons-material/Send';
import { Autocomplete, Box, Button, Grid, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { approveInvoice, getInvoiceBreakdown } from '../../../../redux/slices/invoicing';
import { useDispatch, useSelector } from '../../../../redux/store';
import { AwaitButton } from '../../../../reusable-components/await-button';
import Iconify from '../../../../reusable-components/iconify';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { markMetricRemoved, NotificationMetricType } from '../../../../redux/slices/notifications';

ApproveInvoice.propTypes = {
  goToNext: PropTypes.func,
};

export default function ApproveInvoice({ goToNext }) {
  const { id } = useParams();
  const {
    data: { invoiceApprovers, invoiceItemData },
  } = useSelector((state) => state.invoicing);

  const { invoice } = invoiceItemData || {};
  const { user } = useSelector((state) => state.user);

  const dispatch = useDispatch();

  const [readyForApproval, setReadyForApproval] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [comment, setComment] = useState(''); // Stores the user's comment

  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => {
    const approver = invoiceApprovers?.find((x) => x.approverEmail?.toLowerCase() === user.email?.toLowerCase());
    if (approver) {
      const higherApprovalRequired = invoiceApprovers
        .filter((a) => a.approvalIndex < approver.approvalIndex)
        .some((a) => a.status === 1);
      const alreadyActioned = approver.status !== 1;
      setReadyForApproval(!higherApprovalRequired && !alreadyActioned);
    }
  }, [invoice]);

  const bumpInvoicesNeedApproval = () => dispatch(markMetricRemoved(NotificationMetricType.InvoicesNeedApproval, id));

  const handleApprove = async () => {
    const response = await dispatch(approveInvoice(id));
    if (response === 'success') {
      await dispatch(getInvoiceBreakdown(id));
      enqueueSnackbar('Invoice approved successfully', { variant: 'success' });
      bumpInvoicesNeedApproval();
      goToNext();
    } else {
      enqueueSnackbar('Error approving invoice', { variant: 'error' });
    }
  };
  const handleReject = async () => {
    if (!rejecting) {
      setRejecting(true);
    } else {
      const response = await dispatch(approveInvoice(id, 'Reject', comment));
      if (response === 'success') {
        await dispatch(getInvoiceBreakdown(id));
        enqueueSnackbar('Invoice rejected successfully', { variant: 'success' });
        bumpInvoicesNeedApproval();
        goToNext();
      } else {
        enqueueSnackbar('Error rejecting invoice', { variant: 'error' });
      }
      setRejecting(false);
    }
  };

  return (
    <>
      <Grid container spacing={1} wrap="nowrap">
        <Grid item>
          <Tooltip
            title={!readyForApproval ? 'Awaiting completion by the previous approver in the process.' : ''}
            placement="top"
            arrow
          >
            <Box sx={{ position: 'relative' }}>
              <Button
                variant="outlined"
                color="secondary"
                loadingPosition="start"
                disabled={!readyForApproval}
                size="small"
                onClick={() => {
                  setComment('');
                  setRejecting(!rejecting);
                }}
                endIcon={<Iconify icon="mingcute:down-line" />}
              >
                Reject Invoice
              </Button>
              {rejecting && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    zIndex: 10,
                    background: '#fff',
                    padding: '8px 16px',
                    boxShadow: '0px 4px 6px rgb(17 12 12 / 51%)',
                    width: '300px',
                    borderRadius: '6px',
                    mt: '10px',
                  }}
                >
                  <Grid container alignItems={'center'}>
                    <Grid item flexGrow={1}>
                      <Autocomplete
                        freeSolo
                        options={['Incorrect vendor total', 'Not mine to handle', 'Duplicate Invoice']}
                        value={comment}
                        onInputChange={(event, newValue) => setComment(newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            variant="standard"
                            color="grey"
                            placeholder="Reason for rejecting"
                            multiline
                            autoComplete="off"
                            inputProps={{
                              ...params.inputProps,
                              sx: {
                                '&::placeholder': {
                                  color: 'info.main',
                                  opacity: 0.75,
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item>
                      <Stack>
                        <AwaitButton
                          color="secondary"
                          sx={{ padding: 1, borderRadius: '50%', minWidth: 0 }}
                          onClick={handleReject}
                          id="action-button"
                          className="action-button"
                          disabled={comment === ''}
                        >
                          <SendIcon />
                        </AwaitButton>
                        <IconButton
                          color="error"
                          onClick={() => {
                            setComment('');
                            setRejecting(false);
                          }}
                        >
                          <Typography component="span" sx={{ textDecoration: 'underline', fontSize: '8px' }}>
                            Cancel
                          </Typography>
                        </IconButton>
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          </Tooltip>
        </Grid>
        <Grid item>
          <Tooltip
            title={!readyForApproval ? 'Awaiting completion by the previous approver in the process.' : ''}
            placement="top"
            arrow
          >
            <Box>
              <AwaitButton
                variant="contained"
                color="secondary"
                onClick={handleApprove}
                loadingPosition="start"
                disabled={!readyForApproval}
                size="small"
              >
                Approve Invoice
              </AwaitButton>
            </Box>
          </Tooltip>
        </Grid>
      </Grid>
    </>
  );
}
